class AccessoryCard extends HTMLElement {
  constructor() {
    super();
    this.selectWrappers = this.querySelectorAll('.accessory-card__select-wrapper');
    this.variantInput = this.querySelector('[data-accessory-variant-id]');
    this.submitBtn = this.querySelector('[data-accessory-submit]');
    this.submitText = this.submitBtn ? this.submitBtn.querySelector('span') : null;
    // The whole price row, which also contains the add-to-cart form.
    // Only ever replace the .price element inside it.
    this.priceRow = this.querySelector('[data-accessory-price]');
    this.image = this.querySelector('[data-accessory-image]');
    this.syncValues = {};
    
    try {
      const jsonScript = this.querySelector('[data-variants-json]');
      this.variantData = jsonScript ? JSON.parse(jsonScript.textContent) : [];
    } catch (e) {
      this.variantData = [];
    }

    this.options = [];
    this.selectWrappers.forEach(wrapper => {
      const index = Number.parseInt(wrapper.dataset.optionIndex, 10);
      const select = wrapper.querySelector('select');
      if (Number.isNaN(index) || !select) return;
      this.options[index] = select.value;
      select.addEventListener('change', this.onSelectChange.bind(this));
    });
  }

  onSelectChange(event) {
    const select = event.currentTarget;
    const wrapper = select.closest('.accessory-card__select-wrapper');
    if (!wrapper) return;
    const index = Number.parseInt(wrapper.dataset.optionIndex, 10);
    if (Number.isNaN(index)) return;
    this.options[index] = select.value;

    this.updateVariant();
    this.evaluateMatchStates();
  }

  // Synonyms for size-related option names — these should all match each other
  static SIZE_SYNONYMS = ['mått', 'storlek', 'size', 'bredd', 'längd', 'dimension'];
  static COLOR_SYNONYMS = ['färg', 'color', 'colour', 'kulör'];

  static optionNamesMatch(a, b) {
    const la = a.toLowerCase().trim();
    const lb = b.toLowerCase().trim();
    if (la === lb) return true;
    // Check if both names belong to the same synonym group
    for (const group of [AccessoryCard.SIZE_SYNONYMS, AccessoryCard.COLOR_SYNONYMS]) {
      if (group.includes(la) && group.includes(lb)) return true;
    }
    return false;
  }

  static normalizeValue(v) {
    // Normalize dimension strings: "80x200 cm" ↔ "80 x 200 cm" ↔ "80X200cm"
    return v.toLowerCase().replace(/\s+/g, '').replace(/×/g, 'x');
  }

  // Parse dimension string into {width, length} or null
  static parseDimension(v) {
    const norm = AccessoryCard.normalizeValue(v);
    const m = norm.match(/^(\d+)x(\d+)/);
    if (m) return { width: parseInt(m[1]), length: parseInt(m[2]) };
    return null;
  }

  // Check if an accessory dimension matches the bed dimension
  // Direct match: 80x200 == 80x200
  // Double-width match: bed 80x200 -> accessory 160x200 (two beds side by side)
  static dimensionMatches(bedValue, accessoryValue) {
    const norm1 = AccessoryCard.normalizeValue(bedValue);
    const norm2 = AccessoryCard.normalizeValue(accessoryValue);
    if (norm1 === norm2) return 'exact';

    const bed = AccessoryCard.parseDimension(bedValue);
    const acc = AccessoryCard.parseDimension(accessoryValue);
    if (bed && acc && bed.length === acc.length && acc.width === bed.width * 2) {
      return 'double';
    }
    return false;
  }

  syncOption(optionName, optionValue) {
    this.syncValues[optionName.toLowerCase()] = optionValue.toLowerCase();

    let changed = false;
    const isSizeOption = AccessoryCard.SIZE_SYNONYMS.includes(optionName.toLowerCase().trim());
    const optionContainers = this.querySelectorAll('.accessory-card__option');
    optionContainers.forEach(container => {
      const name = container.dataset.optionName;
      if (name && AccessoryCard.optionNamesMatch(name, optionName)) {
        const select = container.querySelector('select');
        if (!select) return;
        const options = Array.from(select.options);

        // Try exact match first, then normalized match
        let matchOption = options.find(opt => opt.value.toLowerCase() === optionValue.toLowerCase());
        if (!matchOption) {
          const normalizedTarget = AccessoryCard.normalizeValue(optionValue);
          matchOption = options.find(opt => AccessoryCard.normalizeValue(opt.value) === normalizedTarget);
        }

        if (matchOption) {
          // Exact/normalized match — auto-select this option
          container.classList.add('has-match');
          container.classList.remove('has-double-match');
          if (select.value !== matchOption.value) {
            const escapedValue = CSS.escape(matchOption.value);
            const listItem = container.querySelector(`.custom-dropdown-item[data-value="${escapedValue}"]`);
            if (listItem) {
              listItem.click();
              changed = true;
            } else {
              select.value = matchOption.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              changed = true;
            }
          }
        } else if (isSizeOption) {
          // No exact match — check if the currently selected value is a double-width match
          const matchType = AccessoryCard.dimensionMatches(optionValue, select.value);
          if (matchType === 'double') {
            container.classList.add('has-match');
            container.classList.add('has-double-match');
          } else {
            // Check if ANY option in the dropdown is a double-width match
            const doubleMatch = options.find(opt => AccessoryCard.dimensionMatches(optionValue, opt.value) === 'double');
            if (doubleMatch) {
              container.classList.add('has-match');
              container.classList.add('has-double-match');
              // Auto-select the double-width option if no exact match exists
              if (select.value !== doubleMatch.value) {
                const escapedValue = CSS.escape(doubleMatch.value);
                const listItem = container.querySelector(`.custom-dropdown-item[data-value="${escapedValue}"]`);
                if (listItem) {
                  listItem.click();
                  changed = true;
                } else {
                  select.value = doubleMatch.value;
                  select.dispatchEvent(new Event('change', { bubbles: true }));
                  changed = true;
                }
              }
            } else {
              container.classList.remove('has-match');
              container.classList.remove('has-double-match');
            }
          }
        } else {
          container.classList.remove('has-match');
          container.classList.remove('has-double-match');
        }
      }
    });

    // Mark dropdown items with match type for visual highlighting
    this.markDropdownMatches(optionName, optionValue);

    this.evaluateMatchStates();

    if (changed) {
      this.classList.add('flash-sync');
      setTimeout(() => this.classList.remove('flash-sync'), 600);
    }
  }

  markDropdownMatches(optionName, bedValue) {
    const optionContainers = this.querySelectorAll('.accessory-card__option');
    optionContainers.forEach(container => {
      const name = container.dataset.optionName;
      if (!name || !AccessoryCard.optionNamesMatch(name, optionName)) return;

      const isSizeOption = AccessoryCard.SIZE_SYNONYMS.includes(name.toLowerCase().trim());
      const items = container.querySelectorAll('.custom-dropdown-item');

      items.forEach(item => {
        const itemValue = item.getAttribute('data-value');
        if (!itemValue) return;

        // Check exact/normalized match
        const normalizedBed = AccessoryCard.normalizeValue(bedValue);
        const normalizedItem = AccessoryCard.normalizeValue(itemValue);

        if (normalizedBed === normalizedItem || itemValue.toLowerCase() === bedValue.toLowerCase()) {
          item.setAttribute('data-match-type', 'exact');
        } else if (isSizeOption) {
          const dimMatch = AccessoryCard.dimensionMatches(bedValue, itemValue);
          if (dimMatch === 'double') {
            item.setAttribute('data-match-type', 'double');
          } else {
            item.removeAttribute('data-match-type');
          }
        } else {
          item.removeAttribute('data-match-type');
        }
      });
    });
  }

  evaluateMatchStates() {
    const optionContainers = this.querySelectorAll('.accessory-card__option');
    optionContainers.forEach(container => {
      const name = container.dataset.optionName ? container.dataset.optionName.toLowerCase() : null;
      if (!name) return;

      // Find matching sync value using synonym groups
      let syncValue = this.syncValues[name];
      if (!syncValue) {
        for (const [key, val] of Object.entries(this.syncValues)) {
          if (AccessoryCard.optionNamesMatch(name, key)) {
            syncValue = val;
            break;
          }
        }
      }

      if (syncValue) {
        const select = container.querySelector('select');
        if (!select) return;
        const matchType = AccessoryCard.dimensionMatches(syncValue, select.value);
        if (matchType === 'exact' || matchType === 'double') {
          container.classList.add('is-matched');
        } else {
          container.classList.remove('is-matched');
        }
      }
    });
  }

  // No variant exists for the selected combination. Blank the id and block the
  // submit — leaving the previous variant in place would add a different
  // product to the cart than the one the customer picked.
  setUnavailable() {
    if (this.variantInput) this.variantInput.value = '';
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      if (this.submitText) {
        this.submitText.textContent =
          (window.variantStrings && window.variantStrings.unavailable) || 'Ej tillgänglig';
      }
    }
  }

  updateVariant() {
    const variant = this.variantData.find(
      (v) =>
        Array.isArray(v.options) &&
        v.options.length === this.options.length &&
        v.options.every((opt, i) => this.options[i] === opt)
    );

    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.submitBtn) {
      if (variant.available) {
        this.submitBtn.disabled = false;
        if (this.submitText) this.submitText.textContent = window.variantStrings ? window.variantStrings.addToCart : 'Lägg i varukorg';
      } else {
        this.submitBtn.disabled = true;
        if (this.submitText) this.submitText.textContent = window.variantStrings ? window.variantStrings.soldOut : 'Slutsåld';
      }
    }

    // Prices come pre-rendered from Liquid via the shop's money_format. Formatting
    // them here instead would place the currency symbol differently than every
    // server-rendered price on the page.
    if (this.priceRow && variant.priceFormatted) {
      const formattedPrice = variant.priceFormatted;
      let html;
      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        const formattedCompare = variant.compareAtPriceFormatted;
        html = `<div class="price price--on-sale">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="visually-hidden visually-hidden--inline">Ordinarie pris</span>
                      <span class="price-item price-item--regular">${formattedPrice}</span>
                    </div>
                    <div class="price__sale">
                      <span class="visually-hidden visually-hidden--inline">Ordinarie pris</span>
                      <span><s class="price-item price-item--regular">${formattedCompare}</s></span>
                      <span class="visually-hidden visually-hidden--inline">Reapris</span>
                      <span class="price-item price-item--sale price-item--last">${formattedPrice}</span>
                    </div>
                  </div>
                </div>`;
      } else {
        html = `<div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="visually-hidden visually-hidden--inline">Ordinarie pris</span>
                      <span class="price-item price-item--regular">${formattedPrice}</span>
                    </div>
                  </div>
                </div>`;
      }

      // Replace only the price element. The row it sits in also holds the
      // add-to-cart form, so overwriting the whole row destroys the button and
      // the hidden variant input.
      const currentPrice = this.priceRow.querySelector('.price');
      if (currentPrice) currentPrice.outerHTML = html;
    }

    if (variant.imageUrl && this.image) {
      this.image.src = variant.imageUrl;
      if (variant.imageSrcset) {
        this.image.srcset = variant.imageSrcset;
      }
    }
  }
}

customElements.define('accessory-card', AccessoryCard);

class BedAccessoriesTabs {
  constructor(container) {
    this.container = container;
    this.tabs = this.container.querySelectorAll('.bed-accessories__tab');
    this.panels = this.container.querySelectorAll('.bed-accessories__panel');

    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });
  }

  switchTab(clickedTab) {
    const targetId = clickedTab.dataset.target;

    this.tabs.forEach(tab => {
      if (tab === clickedTab) {
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
      } else {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
      }
    });

    this.panels.forEach(panel => {
      if (panel.id === targetId) {
        panel.classList.add('is-active');
      } else {
        panel.classList.remove('is-active');
      }
    });

    // Update panel border-radius based on whether the first tab is active
    const panelsContainer = this.container.querySelector('.bed-accessories__panels');
    const firstTab = this.container.querySelector('.bed-accessories__tab');
    if (panelsContainer && firstTab) {
      if (firstTab.classList.contains('is-active')) {
        panelsContainer.classList.add('first-tab-active');
      } else {
        panelsContainer.classList.remove('first-tab-active');
      }
    }
  }
}

// Tracked in JS rather than as a data-attribute: an attribute travels with the
// markup if it is ever cloned or cached, which would leave the copy uninitialised.
const initialisedContainers = new WeakSet();

// Initialise every instance on the page. The theme editor can render the section
// more than once, and re-renders it without a page load.
function initBedAccessories(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('.bed-accessories__tabs-container').forEach((tabsContainer) => {
    const container = tabsContainer.closest('.page-width') || tabsContainer.parentElement;
    if (!container || initialisedContainers.has(container)) return;
    initialisedContainers.add(container);

    new BedAccessoriesTabs(container);

    // Set initial panel border-radius based on first tab
    const panelsContainer = container.querySelector('.bed-accessories__panels');
    const firstTab = container.querySelector('.bed-accessories__tab');
    if (panelsContainer && firstTab && firstTab.classList.contains('is-active')) {
      panelsContainer.classList.add('first-tab-active');
    }
  });
}

class AccessorySync {
  constructor() {
    document.addEventListener('change', (e) => {
      const isMainForm = Array.from(this.mainForms).some((form) => form.contains(e.target));
      if (isMainForm) {
        this.syncAllAccessories();
      }
    });

    this.scheduleInitialSync();
  }

  // Queried live rather than cached: the theme editor replaces the product form
  // wholesale, which would leave a cached list pointing at detached nodes.
  get mainForms() {
    return document.querySelectorAll('variant-selects, variant-radios');
  }

  // The variant pickers are custom elements and may upgrade after this script
  // runs. Sync at each point where they could have become ready instead of
  // guessing a delay.
  scheduleInitialSync() {
    this.syncAllAccessories();
    requestAnimationFrame(() => this.syncAllAccessories());
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.syncAllAccessories(), { once: true });
    }
  }

  syncAllAccessories() {
    const forms = this.mainForms;
    if (forms.length === 0) return;

    const activeOptions = {};
    forms.forEach(form => {
      const fieldsets = form.querySelectorAll('.product-form__input');
      fieldsets.forEach(fieldset => {
        // Extract option name from the first non-badge span in legend or label
        let name = '';
        const nameSpan = fieldset.querySelector('legend > span:not(.variant-count-badge), label > span:not(.variant-count-badge)');
        if (nameSpan) {
          name = nameSpan.textContent.trim();
        } else {
          const legend = fieldset.querySelector('legend');
          const label = fieldset.querySelector('label');
          if (legend) name = legend.textContent.trim();
          else if (label) name = label.textContent.trim();
          // Strip badge text if present
          if (name) name = name.split('\n')[0].trim();
        }

        let value = '';
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        const select = fieldset.querySelector('select');
        
        if (checkedRadio) value = checkedRadio.value;
        else if (select) value = select.value;

        if (name && value) {
          activeOptions[name] = value;
        }
      });
    });

    const cards = document.querySelectorAll('accessory-card');
    cards.forEach(card => {
      if (typeof card.syncOption !== 'function') return;
      for (const [name, value] of Object.entries(activeOptions)) {
        card.syncOption(name, value);
      }
    });
  }
}

let accessorySync = null;

function bootBedAccessories() {
  initBedAccessories(document);
  if (!accessorySync) accessorySync = new AccessorySync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootBedAccessories, { once: true });
} else {
  bootBedAccessories();
}

// The theme editor swaps section markup in without a page load.
document.addEventListener('shopify:section:load', (event) => {
  initBedAccessories(event.target);
  if (accessorySync) accessorySync.syncAllAccessories();
});
